import {Injectable} from '@angular/core'; 
import {Observable, of, throwError} from 'rxjs'; 
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http'; 
import {ApiModel} from '../classes/api_model' 
import {Posts} from '../classes/posts' 
import { TokenStorageService } from './token-storage.service';
import { environment } from 'src/environments/environment';
import {
    catchError,
    map,
    tap,
    shareReplay,
    retryWhen,
    delay,
    take,
    mergeMap,
    switchMap,
    filter,
    first
} from 'rxjs/operators';

// Cache configuration
interface CacheConfig {
    ttl?: number; // Time to live in milliseconds
    useSessionStorage?: boolean; // Whether to persist to session storage
    invalidateOn?: string[]; // Events that invalidate this cache
}
// Default cache configuration
const DEFAULT_CACHE_CONFIG: CacheConfig = {
    ttl: 300000, // 5 minutes
    useSessionStorage: false
};
 
@Injectable() 
export class swaggerApiService { 

    private memoryCache = new Map<string, { data: any; expiry: number }>();
    private pendingRequests = new Map<string, Observable<any>>();
    private decryptedLibraryCache = new Map<string, { data: any; expiry: number }>();
    private performanceMetrics = new Map<string, number[]>();
    private cacheEvents = new Map<string, Set<string>>();

    //PROD -----------
    private apiBaseUrl = (environment as any).apiBaseUrl || 'https://api.sathachak.com';
    private basePublicUserUrl = environment.publicUserApiBase || `${this.apiBaseUrl}/public_users`;
    
    constructor(
        private httpClient:HttpClient,
        private tokenStorageService: TokenStorageService
    ){} 

     // ========================
    // 🚀 Advanced Cache System
    // ========================

     private getCacheKey(method: string, params: any = {}): string {
        const paramString = JSON.stringify(params);
        return `${method}_${paramString}`;
    }

      private getFromCache(key: string, config: CacheConfig = DEFAULT_CACHE_CONFIG): any | null {
        // Check memory cache first
        const memoryItem = this.memoryCache.get(key);
        if (memoryItem && memoryItem.expiry > Date.now()) {
            return memoryItem.data;
        }

        // Check session storage if configured
        if (config.useSessionStorage) {
            try {
                const sessionItem = sessionStorage.getItem(key);
                if (sessionItem) {
                    const { data, expiry } = JSON.parse(sessionItem);
                    if (expiry > Date.now()) {
                        // Also store in memory for faster access
                        this.memoryCache.set(key, { data, expiry });
                        return data;
                    }
                    sessionStorage.removeItem(key);
                }
            } catch (e) {
                console.error('Session storage access failed', e);
            }
        }

        return null;
    }
 
     postPageToken():Observable<any>{ 
        return this.httpClient.post("https://lexicon.legalcouncilmef.com/dev/page-access-token/get_page_token/",{type:'sathachak_page'},{responseType: 'json'}); 
    } 
 
    postOtherTab(objBody:ApiModel):Observable<any>{ 
        // console.log("objBody ==", objBody); 
 
        return this.httpClient.post("https://app.legalcouncilmef.com/other_tab/get_list/",objBody,{responseType: 'json'}); 
    } 
 
    getOtherTabCategory():Observable<any>{ 
        return this.httpClient.get("https://app.legalcouncilmef.com/other_tab_category/",{responseType: 'json'}); 
    } 
 
    postDMISCategory(cate:boolean):Observable<any>{ 
        return this.httpClient.get("https://dmis.legalcouncilmef.com/api/type_of_legal/"+cate,{responseType: 'json'}); 
    } 
 
    postDMISDocuments(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post("https://dmis.legalcouncilmef.com/api/law/get_list_encrypt",objBody,{responseType: 'text'}); 
    } 
 
    getNewsFB(uri): Observable<any> {    
        // this.http refers to HttpClient. Note here that you cannot use the generic get<Blob> as it does not compile: instead you "choose" the appropriate API in this way. 
        return this.httpClient.get(uri, { responseType: 'json' }); 
    } 
 
    getPDF(uri): Observable<Blob> {    
        // this.http refers to HttpClient. Note here that you cannot use the generic get<Blob> as it does not compile: instead you "choose" the appropriate API in this way. 
        return this.httpClient.get(uri, { responseType: 'blob' }); 
    } 
 
    postELibrary(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/library/get_all_elib`,objBody,{responseType: 'text'}); 
    } 
 
    postELibraryTypes(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/library/get_all_elib_v2`,objBody,{responseType: 'text'}); 
    } 
 
    postOtherDoc(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post("https://app.legalcouncilmef.com/otherdoc/get_list",objBody,{responseType: 'text'}); 
    } 
 
    postEduvideo(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post("https://app.legalcouncilmef.com/eduvideo/get_list",objBody,{responseType: 'text'}); 
    } 
 
    postCategory(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post("https://app.legalcouncilmef.com/category/get_list",objBody,{responseType: 'text'}); 
    } 
 
    postCarousel(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/sliding/get_list`,objBody,{responseType: 'text'}); 
    } 
 
    postGetLibraryId(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/library/find_id`,objBody,{responseType: 'text'}); 
    } 
 
    postGetNewsId(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/news/find_id`,objBody,{responseType: 'text'}); 
    } 
 
    postLibraryGetLists(objBody:ApiModel):Observable<any>{ 
        // The list endpoint validates the inherited id field as a required string.
        const body = { ...objBody, id: objBody.id == null ? '' : objBody.id };
        return this.httpClient.post(`${this.apiBaseUrl}/library/get_all_elib_v2`, objBody, { responseType: 'text' }); 
    } 
    postNewsGetLists(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/news/get_list`,objBody,{responseType: 'text'}); 
    } 
    postDecrypted(objBody:ApiModel):Observable<any>{ 
        return this.httpClient.post(`${this.apiBaseUrl}/encrypt/decrypted`,objBody,{responseType: 'json'}); 
    } 
    posts(objPost:Posts):Observable<any>{ 
        return this.httpClient.post("https://jsonplaceholder.typicode.com/posts",objPost); 
 
    } 



     postLogin(loginData: any): Observable<any> {
        this.clearCacheByEvent('login');
        return this.makeRequest('POST', `${this.basePublicUserUrl}/login`, {
            body: loginData
        });
    }

    registerUser(data: FormData): Observable<any> {
        return this.makeRequest('POST', `${this.basePublicUserUrl}/register`, {
            body: data
        });
    }

    sendOtp(email: string): Observable<any> {
        return this.makeRequest('POST', `${this.basePublicUserUrl}/send-otp`, {
            body: { email }
        });
    }

    verifyOtp(email: string, otp: string): Observable<any> {
        return this.makeRequest('POST', `${this.basePublicUserUrl}/verify-otp`, {
            body: { email, otp }
        });
    }

    resetPassword(email: string): Observable<any> {
        return this.makeRequest('POST', `${this.basePublicUserUrl}/request-password-reset`, {
            body: { email }
        });
    }

    userProfile(): Observable<any> {
        const token = this.tokenStorageService.getAccessToken();
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
        return this.makeRequest('GET', `${this.basePublicUserUrl}/profile`, {
            headers,
            cacheConfig: {
                ttl: 60000, // 1 minute
                invalidateOn: ['profile_update', 'login', 'logout']
            }
        });
    }

    updateUserProfile(id: any, data: FormData): Observable<any> {
        const token = this.tokenStorageService.getAccessToken();
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
        this.clearCacheByEvent('profile_update');
        return this.makeRequest('GET', `${this.basePublicUserUrl}/profile/${id}`, {
            headers
        });
    }

    private clearCacheByEvent(event: string): void {
        const keysToInvalidate = this.cacheEvents.get(event) || new Set();
        keysToInvalidate.forEach(key => {
            this.memoryCache.delete(key);
            sessionStorage.removeItem(key);
        });
        this.cacheEvents.delete(event);
    }


     // ========================
    // 🛠️ API Request Wrapper
    // ========================

    private makeRequest<T>(
        method: 'GET' | 'POST',
        url: string,
        options: {
            body?: any;
            params?: HttpParams;
            headers?: HttpHeaders;
            responseType?: any;
            cacheConfig?: CacheConfig;
        } = {}
    ): Observable<T> {
        const startTime = performance.now();
        const cacheKey = this.getCacheKey(url, options.body || options.params);
        const cachedData = options.cacheConfig ? this.getFromCache(cacheKey, options.cacheConfig) : null;

        if (cachedData) {
            return of(cachedData);
        }

        const request$ = this.deduplicateRequest(cacheKey, () => {
            let httpCall: Observable<T>;
            const requestOptions = {
                headers: options.headers,
                params: options.params,
                responseType: options.responseType
            };

            if (method === 'GET') {
                httpCall = this.httpClient.get<T>(url, requestOptions);
            } else {
                httpCall = this.httpClient.post<T>(url, options.body, requestOptions);
            }

            return this.withRetry(httpCall).pipe(
                tap(response => {
                    if (options.cacheConfig) {
                        this.saveToCache(cacheKey, response, options.cacheConfig);
                        if (options.cacheConfig.invalidateOn) {
                            this.registerCacheEvent(cacheKey, options.cacheConfig.invalidateOn);
                        }
                    }
                    const duration = performance.now() - startTime;
                    this.trackPerformance(url, duration);
                })
            );
        });

        return request$;
    }

     // ========================
    // 🔄 Request Deduplication
    // ========================

    private deduplicateRequest<T>(
        key: string,
        requestFn: () => Observable<T>
    ): Observable<T> {
        // If we already have a pending request for this key, return it
        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key)! as Observable<T>;
        }

        // Otherwise create a new request
        const request$ = requestFn().pipe(
            tap(() => this.pendingRequests.delete(key)),
            catchError(err => {
                this.pendingRequests.delete(key);
                return throwError(err);
            }),
            shareReplay(1) // Share the result with all subscribers
        );

        this.pendingRequests.set(key, request$);
        return request$;
    }

      // ========================
    // 🔄 Retry with Exponential Backoff
    // ========================

    private withRetry<T>(observable: Observable<T>, maxRetries = 3): Observable<T> {
        return observable.pipe(
            retryWhen(errors =>
                errors.pipe(
                    mergeMap((error, i) => {
                        const retryAttempt = i + 1;
                        if (retryAttempt > maxRetries || error.status < 500) {
                            return throwError(error);
                        }
                        const delayTime = Math.pow(2, retryAttempt) * 1000;
                        return of(error).pipe(delay(delayTime));
                    })
                )
            )
        );
    }
     private saveToCache(key: string, data: any, config: CacheConfig = DEFAULT_CACHE_CONFIG): void {
        const expiry = Date.now() + (config.ttl || DEFAULT_CACHE_CONFIG.ttl!);
        const cacheItem = { data, expiry };

        // Store in memory
        this.memoryCache.set(key, cacheItem);

        // Store in session storage if configured
        if (config.useSessionStorage) {
            try {
                sessionStorage.setItem(key, JSON.stringify(cacheItem));
            } catch (e) {
                console.error('Session storage quota exceeded', e);
            }
        }
    }

      private registerCacheEvent(key: string, events: string[]): void {
        events.forEach(event => {
            if (!this.cacheEvents.has(event)) {
                this.cacheEvents.set(event, new Set());
            }
            this.cacheEvents.get(event)!.add(key);
        });
    }

      // ========================
    // 📊 Performance Tracking
    // ========================

    private trackPerformance(endpoint: string, duration: number): void {
        if (!this.performanceMetrics.has(endpoint)) {
            this.performanceMetrics.set(endpoint, []);
        }
        const metrics = this.performanceMetrics.get(endpoint)!;
        metrics.push(duration);
        if (metrics.length > 100) {
            metrics.shift(); // Keep only the last 100 measurements
        }
    }

    getFeedback(elibId: string): Observable<any> {
        return this.makeRequest('GET', `${this.apiBaseUrl}/feedback/${elibId}`, {
            responseType: 'text',
            cacheConfig: {
                ttl: 60000, // 1 minute
                invalidateOn: ['feedback_update']
            }
        });
    }

    postFeedback(objBody: FormData): Observable<any> {
        this.clearCacheByEvent('feedback_update');
        return this.makeRequest('POST', `${this.apiBaseUrl}/feedback/create`, {
            body: objBody
        });
    }
     getRelatedElibCategory(subCategory: string): Observable<any> {
        const categoryId = typeof subCategory === 'string' ? subCategory.trim() : '';
        if (!categoryId || categoryId === 'undefined' || categoryId === 'null') {
            return of([]);
        }
        const params = new HttpParams().set('subCategory', categoryId);
        return this.makeRequest('POST', `${this.apiBaseUrl}/elib_category/related`, {
            params,
            cacheConfig: {
                ttl: 86400000, // 24 hours
                useSessionStorage: true,
                invalidateOn: ['content_update']
            }
        });
    }
}
